import * as z from 'zod';

import {
  CompanyId,
  CompanyReportId,
  InvestorReportingId,
  LoanId,
  LoanOwnerId,
  LosId,
  PoolId,
  RecurringCompanyReportId,
  UserId,
} from './BrandedIds';
import { CompanyReportDownload } from './CompanyReportDownload';
import { MonthYear } from './CreditReport';
import { DisbursementPayeeType } from './loan/Disbursement';
import { LoanStatus } from './loan/LoanStatus';
import { WorkoutPlanStatus, WorkoutPlanType } from './loan/WorkoutPlan';
import { IsoDateFilter } from './loanTransactionFilter/LoanTransactionFilter';
import { zodBrandedString } from './utils/Zod';

export interface CompanyReport {
  id: CompanyReportId;
  userId: UserId;
  companyId: CompanyId;
  title: string;
  location?: string;
  loanCount?: number;
  recordCount?: number;
  type: CompanyReportType;
  recurring: boolean;
  recurringCompanyReportId?: RecurringCompanyReportId;
  fromDate?: Date;
  toDate?: Date;
  exactDate?: Date;
  MonthYear?: MonthYear;
  createdAt: Date;
  updatedAt: Date;
  sftpSentStatus?: SftpSentStatus;
  sftpError?: string;
  status: CompanyReportStatus;
  idempotencyKey?: string;
  lastHeartbeatAt?: Date;
  downloads: CompanyReportDownload[];
}

export enum SftpSentStatus {
  SENT = 'SENT',
  NOT_SENT = 'NOT_SENT',
  ERROR = 'ERROR',
}

/**
 * Lifecycle status for company reports.
 *
 * PENDING and IN_PROGRESS are set during report generation. COMPLETED is set
 * when the report is fully generated and uploaded. FAILED is reserved for
 * explicit failures. TIMED_OUT is derived at query time (never stored in DB)
 * when a report is IN_PROGRESS but its heartbeat is stale.
 */
export const CompanyReportStatus = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  TIMED_OUT: 'TIMED_OUT',
} as const;

export type CompanyReportStatus = (typeof CompanyReportStatus)[keyof typeof CompanyReportStatus];

export enum CompanyReportType {
  CASH_MANAGEMENT = 'CASH_MANAGEMENT',
  INVESTOR_REMITTANCE = 'INVESTOR_REMITTANCE',
  INVESTOR_REMITTANCE_NEW = 'INVESTOR_REMITTANCE_NEW',
  INVESTOR_REMITTANCE_FEES = 'INVESTOR_REMITTANCE_FEES',
  INVESTOR_REMITTANCE_COMPILED = 'INVESTOR_REMITTANCE_COMPILED',
  PAYOFFS = 'PAYOFFS',
  TRANSACTION_HISTORY = 'TRANSACTION_HISTORY',
  LOAN_NOTES = 'LOAN_NOTES',
  SERVICING_TRANSFER = 'SERVICING_TRANSFER',
  LOANS_BILLED = 'LOANS_BILLED',
  PAYMENT_SUMMARY = 'PAYMENT_SUMMARY',
  LOAN_PIPELINE = 'LOAN_PIPELINE',
  ESCROW_DISBURSEMENTS = 'ESCROW_DISBURSEMENTS',
  AUDIT_LOG = 'AUDIT_LOG',
  AUTHORIZED_ACH_BATCH = 'AUTHORIZED_ACH_BATCH',
  ACH_TRANSACTIONS = 'ACH_TRANSACTIONS',
  FAILED_ACH_TRANSACTIONS = 'FAILED_ACH_TRANSACTIONS',
  LOAN_PRODUCTIVITY = 'LOAN_PRODUCTIVITY',
  TRIAL_BALANCE = 'TRIAL_BALANCE',
  TRIAL_BALANCE_SUMMARY = 'TRIAL_BALANCE_SUMMARY',
  LOAN_EXTENSION = 'LOAN_EXTENSION',
  PRINCIPAL_REDUCTION = 'PRINCIPAL_REDUCTION',
  CREDIT_REPORT = 'CREDIT_REPORT',
  GOODBYE_LETTERS = 'GOODBYE_LETTERS',
  FEE_BALANCE = 'FEE_BALANCE',
  LOANS_BOARDED = 'LOANS_BOARDED',
  ADVANCES = 'ADVANCES',
  BORROWER_AUTHORIZED_ACH = 'BORROWER_AUTHORIZED_ACH',
  MERS_RECONCILIATION = 'MERS_RECONCILIATION',
  DRAWS = 'DRAWS',
  REINSTATEMENT = 'REINSTATEMENT',
  LOSS_MITIGATION = 'LOSS_MITIGATION',

  GENERAL_LEDGER_CODES = 'GENERAL_LEDGER_CODES', // TODO categorize

  // Lower reports
  COMPILED_ACCRUALS_AND_PAYMENTS = 'COMPILED_ACCRUALS_AND_PAYMENTS',
  LOAN_TRANSFER_DOCUMENTS = 'LOAN_TRANSFER_DOCUMENTS',
  LOS_BALANCE_AND_TRANSACTIONS = 'LOS_BALANCE_AND_TRANSACTIONS',
  PAST_DUE_LOANS = 'PAST_DUE_LOANS',
  NON_PERFORMING_LOANS = 'NON_PERFORMING_LOANS',
  SHELLPOINT_HELOCS = 'SHELLPOINT_HELOCS',

  // Kensie Mae / Interline reports
  KENSIE_MAE_PAYMENT = 'KENSIE_MAE_PAYMENT',
  KENSIE_MAE_ESCROW_DISBURSEMENT = 'KENSIE_MAE_ESCROW_DISBURSEMENT',
  KENSIE_MAE_PRINCIPAL_PREPAYMENT = 'KENSIE_MAE_PRINCIPAL_PREPAYMENT',

  // Vontive reports
  ONE_80_INSURANCE = 'ONE_80_INSURANCE',

  // Renovo reports
  SERVICING_TRANSFER_CUSTOM_RO = 'SERVICING_TRANSFER_CUSTOM_RO',
  SERVICING_TRANSFER_COMPILED_PACKAGE = 'SERVICING_TRANSFER_COMPILED_PACKAGE',
  PROCTOR_INSURANCE = 'PROCTOR_INSURANCE',

  // Elite reports
  ALLIED_INSURANCE = 'ALLIED_INSURANCE',
  CBRE_LOAN_TRACKING = 'CBRE_LOAN_TRACKING',
  SUI_LOAN_TRACKING = 'SUI_LOAN_TRACKING',
  STATEMENT_OF_ACCOUNT = 'STATEMENT_OF_ACCOUNT',
  PAYOFF_REIMBURSEMENT = 'PAYOFF_REIMBURSEMENT',

  // Ark reports
  MCT_REPORT = 'MCT_REPORT',

  // Trio reports
  SPERLONGA_CREDIT_REPORT = 'SPERLONGA_CREDIT_REPORT',

  // Wheda reports
  BORROWER_LOGIN = 'BORROWER_LOGIN',
  FHA_MONTHLY_PREMIUM = 'FHA_MONTHLY_PREMIUM',
  FHA_TS_264 = 'FHA_TS_264',
  FHA_TS_264_EDIT = 'FHA_TS_264_EDIT',
  FHA_TS_260 = 'FHA_TS_260',
  FHA_TS_266 = 'FHA_TS_266',
  CHECK_DISBURSEMENTS_FILE = 'CHECK_DISBURSEMENTS_FILE',
  DISBURSEMENT_DETAIL_FOR_MI_ESCROW = 'DISBURSEMENT_DETAIL_FOR_MI_ESCROW',
  DISBURSEMENT_RECONCILIATION = 'DISBURSEMENT_RECONCILIATION',
  MILAR_ABBREVIATED_AND_FULL = 'MILAR_ABBREVIATED_AND_FULL',
  ADR = 'ADR',
  FREDDIE_MAC_LLR = 'FREDDIE_MAC_LLR',
  FREDDIE_MAC_LLR_EDIT = 'FREDDIE_MAC_LLR_EDIT',
  FREDDIE_MAC_LLR_DAILY = 'FREDDIE_MAC_LLR_DAILY',
  FREDDIE_MAC_LLR_MONTHLY = 'FREDDIE_MAC_LLR_MONTHLY',
  FREDDIE_MAC_LLR_TRAILING = 'FREDDIE_MAC_LLR_TRAILING',
  FREDDIE_MAC_EDR = 'FREDDIE_MAC_EDR',
  FREDDIE_MAC_EDR_EDIT = 'FREDDIE_MAC_EDR_EDIT',
  FREDDIE_MAE_SDC = 'FREDDIE_MAE_SDC',
  FANNIE_MAE_CRS_DRAFT_REQUEST = 'FANNIE_MAE_CRS_DRAFT_REQUEST',
  FANNIE_MAE_CRS_DRAFT_REQUEST_EDIT = 'FANNIE_MAE_CRS_DRAFT_REQUEST_EDIT',
  FANNIE_MAE_LAR_96 = 'FANNIE_MAE_LAR_96',
  FANNIE_MAE_LAR_96_EDIT = 'FANNIE_MAE_LAR_96_EDIT',
  FANNIE_MAE_LAR_96_DAILY = 'FANNIE_MAE_LAR_96_DAILY',
  FANNIE_MAE_LAR_96_MONTHLY = 'FANNIE_MAE_LAR_96_MONTHLY',
  FANNIE_MAE_LAR_96_TRAILING = 'FANNIE_MAE_LAR_96_TRAILING',
  FANNIE_MAE_LAR_89 = 'FANNIE_MAE_LAR_89',
  FANNIE_MAE_LAR_89_EDIT = 'FANNIE_MAE_LAR_89_EDIT',
  FANNIE_MAE_LAR_32 = 'FANNIE_MAE_LAR_32',
  FANNIE_MAE_LAR_32_EDIT = 'FANNIE_MAE_LAR_32_EDIT',
  FANNIE_MAE_LAR_81 = 'FANNIE_MAE_LAR_81',
  FANNIE_MAE_LAR_81_EDIT = 'FANNIE_MAE_LAR_81_EDIT',
  FANNIE_MAE_LAR_83 = 'FANNIE_MAE_LAR_83',
  FANNIE_MAE_LAR_83_EDIT = 'FANNIE_MAE_LAR_83_EDIT',
  FANNIE_MAE_DELINQUENCY = 'FANNIE_MAE_DELINQUENCY',
  FANNIE_MAE_DELINQUENCY_EDIT = 'FANNIE_MAE_DELINQUENCY_EDIT',
  GINNIE_RFS = 'GINNIE_RFS',
  GINNIE_RFS_EDIT = 'GINNIE_RFS_EDIT',
  GINNIE_PDS = 'GINNIE_PDS',
  P_AND_I_ADVANCES = 'P_AND_I_ADVANCES',
  P_AND_I_INVESTOR_RECEIVABLE = 'P_AND_I_INVESTOR_RECEIVABLE',
  P_AND_I_RECONCILIATION = 'P_AND_I_RECONCILIATION',
  T_AND_I_RECONCILIATION = 'T_AND_I_RECONCILIATION',
  MORTGAGE_INSURANCE_CANCELLATION = 'MORTGAGE_INSURANCE_CANCELLATION',
  BORROWER_CONTACT = 'BORROWER_CONTACT',
  DELINQUENCY_AGING = 'DELINQUENCY_AGING',
  BANKRUPTCY_LOAN_STATUS = 'BANKRUPTCY_LOAN_STATUS',
  LOSS_MITIGATION_STATUS = 'LOSS_MITIGATION_STATUS',
  LOAN_MODIFICATION_SUCCESS_RATE = 'LOAN_MODIFICATION_SUCCESS_RATE',
  FORECLOSURE_STATUS = 'FORECLOSURE_STATUS',
  DATA_EXPORT = 'DATA_EXPORT',
  INTEREST_ON_ESCROW = 'INTEREST_ON_ESCROW',
  BORROWER_EMAIL_LOG = 'BORROWER_EMAIL_LOG',
}

export const DateFilterField = z.enum(['transferEffectiveDate', 'transferSoldDate', 'paymentProcessedDate']);

export type DateFilterField = z.infer<typeof DateFilterField>;

export const RemittanceDateFilterField = z.enum(['processedDate', 'remittableDate']);

export type RemittanceDateFilterField = z.infer<typeof RemittanceDateFilterField>;

export const NonPerformingReportStatusFilter = z.enum(['all', 'foreclosure', 'bankruptcy']);
export type NonPerformingReportStatusFilter = z.infer<typeof NonPerformingReportStatusFilter>;

export const CompanyReportFilters = z
  .object({
    losIds: z.array(z.custom<LosId>()),
    poolIds: z.array(PoolId),
    ownerIds: z.array(zodBrandedString<LoanOwnerId>()),
    servicerNames: z.array(z.string()).optional(),
    userIds: z.array(zodBrandedString<UserId>()).optional(),
    fullIds: z.array(z.string()),
    capitalPartnerIds: z.array(z.string()),
    otherId1s: z.array(z.string()),
    previousServicerIds: z.array(z.string()),
    loanStatuses: z.array(LoanStatus),
    dateFilterField: DateFilterField.optional(),
    remittanceDateFilterField: RemittanceDateFilterField.optional(),
    reportingDaysDelta: z.number().int().gte(-7).lte(7).default(0),
    pastDue: z
      .object({ minDays: z.number().positive().optional(), maxDays: z.number().positive().optional() })
      .optional(),
    monthYear: MonthYear.optional(),
    workoutPlan: z
      .object({
        types: z.array(z.enum(WorkoutPlanType.options)).optional(),
        statuses: z.array(z.enum(WorkoutPlanStatus.options)).optional(),
      })
      .optional(),
    payeeNames: z.array(z.string()).optional(),
    payeeType: DisbursementPayeeType.optional(),
    nonPerformingStatusFilter: NonPerformingReportStatusFilter.optional(),
  })
  .merge(IsoDateFilter);

export type CompanyReportFilters = z.infer<typeof CompanyReportFilters>;

export const REPORT_DATE_RANGE_MAX_MONTHS_ONE = 1;

export type CreateReportResponse = {
  reportFile: Buffer;
  meta: {
    recordCount: number;
    loanCount: number;
    loanIds: LoanId[];
    minDate: Date;
    maxDate: Date;
  };
  investorReportingMetadata?: {
    investorReportingIds: InvestorReportingId[];
  };
};
